import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import {CardNumberElement} from '@stripe/react-stripe-js';
import loadable from "../Lazy";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Collapse from '@material-ui/core/Collapse';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import PaymentMethodErrorModal from "../Modals/PaymentMethodErrorModal";
import {setFooterOpen, setModal, setScrollToElement} from "../../actions/app";
import {updateGuestCheckout, updateGuestCheckoutReset} from "../../actions/guestCheckout";
import {connect} from 'react-redux';
import {useHistory} from "react-router-dom";
import {updateBillingAddress, updateBillingAddressReset} from "../../actions/stripe";
import { Alert } from '@material-ui/lab';

const Button = loadable(() => import('../../components/Button/LoadingButton'));
const WalletButton = loadable(() => import('./PaymentPicker/PaymentPickerWalletButton'))
const PaymentPickerCreditCard = loadable(() => import('./PaymentPicker/PaymentPickerCreditCard'))

const useStyles = makeStyles((theme) => ({
    '@global': {
        body: {
            backgroundColor: theme.palette.grey[200]
        }
    },
    card: {
        marginBottom: theme.spacing(2)
    },
    root: {
        width: '100%',
        backgroundColor: theme.palette.background.paper
    },
    collapseContent: {
        // border: `solid 1px #CCCCCC`,
        padding: theme.spacing(2),
        // paddingLeft: theme.spacing(9),
        width: '100%',
        marginBottom: theme.spacing(2)
    },
    listItem: {
        border: `solid 1px #CCCCCC`,
        marginBottom: theme.spacing(1)
    }
}));

const GuestCheckoutPaymentPicker = ({
    guestCheckout,
    setScrollToElement,
    setModal,
    updateGuestCheckout,
    updateGuestCheckoutReset,
    updateGuestCheckoutSuccess,
    updateGuestCheckoutError,
    savedPaymentMethods = {},
    secureCheckout = false,
    updateBillingAddress,
    updateBillingAddressSuccess,
    updateBillingAddressError,
    updateBillingAddressReset
}) => {
    const classes = useStyles();
    let history = useHistory();
    const [savedCardUpdateBillingAddressError, setSavedCardUpdateBillingAddressError] = useState(false);
    const [errorMessageOpen, setErrorMessageOpen] = React.useState(false);
    const [creditCardErrorMessage, setCreditCardErrorMessage] = useState('');
    const [savedCardId, setSavedCardId] = useState("choose");
    const [expanded, setExpanded] = React.useState(guestCheckout.paymentMethod ? guestCheckout.paymentMethod : null);

    const handleAccordionChange = (value) => {
        if (expanded === value) {
            setExpanded(null);
            return;
        }
        setExpanded(value);
    };

    const handleSavedCardsChange = (e) => {
        setSavedCardId(e.target.value);
    };

    const redirectToURL = (url) => {
        if (secureCheckout) {
            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    const handleNextWithSavedCard = (stripe, elements, values) => {
        setSavedCardUpdateBillingAddressError(false);

        if (savedPaymentMethods[savedCardId] && savedPaymentMethods[savedCardId].hasAddress) {

            updateGuestCheckout({
                paymentMethod: 'credit_card',
                billingAccountId: savedCardId,
                ccType: savedPaymentMethods[savedCardId].type,
                ccLastFour: savedPaymentMethods[savedCardId].lastFour,
                recaptchaResponse: '',
                stripePaymentMethodId: savedCardId
            });
        } else {
            updateBillingAddress({
                'payment_method_id': savedCardId,
                'address_line1': values.billingAddress1,
                'address_line2': values.billingAddress2,
                'locality': values.billingCity,
                'region': values.billingState,
                'postal_code': values.billingZip
            })
        }
    }

    const handleNextWithPayPal = () => {
        updateGuestCheckout({
            paymentMethod: 'paypal',
            paypalAuthorizationId: ''
        });
    }

    React.useEffect(() => {
        if (updateBillingAddressSuccess) {
            updateGuestCheckout({
                paymentMethod: 'credit_card',
                billingAccountId: savedCardId,
                ccType: savedPaymentMethods[savedCardId].type,
                ccLastFour: savedPaymentMethods[savedCardId].lastFour,
                recaptchaResponse: '',
                stripePaymentMethodId: savedCardId
            });
            updateBillingAddressReset();
        }
    }, [updateBillingAddressSuccess, savedCardId, savedPaymentMethods, updateBillingAddressReset, updateGuestCheckout]);

    React.useEffect(() => {
        if (updateBillingAddressError) {
            setSavedCardUpdateBillingAddressError(true);
            updateBillingAddressReset();
        }
    }, [updateBillingAddressError, updateBillingAddressReset]);

    React.useEffect(() => {
        if (updateGuestCheckoutSuccess) {
            updateGuestCheckoutReset();
            const paymentDetailsDone = guestCheckout.billingAccountId || guestCheckout.stripePaymentMethodId || guestCheckout.paymentMethod === 'paypal';

            if (secureCheckout) {
                if (!paymentDetailsDone) {
                    redirectToURL('/secure/checkout/payment')
                    return;
                }
                redirectToURL('/secure/checkout')
            } else {
                if (!paymentDetailsDone) {
                    redirectToURL('/guest-checkout/payment')
                    return;
                }
                redirectToURL('/guest-checkout/summary')
            }
        }
        // eslint-disable-next-line
    }, [updateGuestCheckoutSuccess]);

    React.useEffect(() => {
        if (updateGuestCheckoutError) {
            updateGuestCheckoutReset();
            setErrorMessageOpen(true);
        }
    }, [updateGuestCheckoutError, updateGuestCheckoutReset]);

    useEffect(() => {
        setTimeout(() => {
            setScrollToElement('body');
        }, 400);
    }, [expanded, setScrollToElement]);

    return (
        <Card className={classes.card}>
            <CardContent>
                <div className={classes.root}>

                    {errorMessageOpen && (
                    <Alert severity="error">
                        We are sorry! It appears an error occurred. Please try again or contact customer support.
                    </Alert>
                    )}

                    <List
                        component="nav"
                        aria-labelledby="nested-list-subheader"
                        className={classes.root}
                    >

                        {Object.keys(savedPaymentMethods).length > 0 && (
                            <>
                            <ListItem className={classes.listItem} button selected={expanded === 'saved'} onClick={() => {handleAccordionChange('saved');}}>
                                <ListItemIcon>
                                    <img alt="" style={{maxWidth: '46px'}} src="https://res.cloudinary.com/giftya-llc/image/upload/v1667333700/Design%20Assets/Icons/card_djjjtj.png" />
                                </ListItemIcon>
                                <ListItemText primary={`Saved Credit Cards`}/>
                                <ListItemSecondaryAction onClick={() => {handleAccordionChange('saved');}}>
                                    {expanded === 'saved' ? <ExpandLess /> : <ExpandMore />}
                                </ListItemSecondaryAction>
                            </ListItem>
                            <Collapse in={expanded === 'saved'} timeout="auto" unmountOnExit={false}>
                                <div className={classes.collapseContent}>

                                    <div style={{marginBottom: '16px'}}>

                                        {savedCardUpdateBillingAddressError && (
                                            <Alert severity="info" style={{marginBottom: '16px'}}>
                                                <Typography>We are sorry. There was an error submitting the form. Please try again later or contact customer support.</Typography>
                                            </Alert>
                                        )}

                                        <TextField
                                            select
                                            fullWidth={true}
                                            label="Saved cards"
                                            value={savedCardId}
                                            onChange={handleSavedCardsChange}
                                            SelectProps={{
                                                native: true,
                                            }}
                                            // helperText="Please select saved card"
                                            variant="filled"
                                        >
                                            <option value="choose">Choose a saved card</option>
                                            {Object.keys(savedPaymentMethods).map(paymentMethodId => (
                                                <option key={paymentMethodId} value={paymentMethodId}>
                                                    {savedPaymentMethods[paymentMethodId].label}
                                                </option>
                                            ))}
                                        </TextField>
                                    </div>

                                    {savedPaymentMethods[savedCardId] && !savedPaymentMethods[savedCardId].hasAddress && (
                                        <PaymentPickerCreditCard
                                            includeCreditCardFields={false}
                                            guestCheckout={guestCheckout}
                                            handleSubmit={handleNextWithSavedCard}
                                        />
                                    )}

                                    {savedPaymentMethods[savedCardId] && savedPaymentMethods[savedCardId].hasAddress && (
                                        <Button
                                            style={{color: 'white'}}
                                            onClick={handleNextWithSavedCard}
                                            variant="contained"
                                            fullWidth={true}
                                            color="primary"
                                        >Next</Button>
                                    )}
                                </div>
                            </Collapse>
                            </>
                        )}





                        <ListItem className={classes.listItem} button selected={expanded === 'credit_card'} onClick={() => {handleAccordionChange('credit_card');}}>
                            <ListItemIcon>
                                <img alt="" style={{maxWidth: '46px'}} src="https://res.cloudinary.com/giftya-llc/image/upload/v1667333700/Design%20Assets/Icons/card_djjjtj.png" />
                            </ListItemIcon>
                            <ListItemText primary={`${Object.keys(savedPaymentMethods).length > 0 ? "Add " : ""}Credit Card`}/>
                            <ListItemSecondaryAction onClick={() => {handleAccordionChange('credit_card');}}>
                                {expanded === 'credit_card' ? <ExpandLess /> : <ExpandMore />}
                            </ListItemSecondaryAction>
                        </ListItem>
                        <Collapse in={expanded === 'credit_card'} timeout="auto" unmountOnExit={false}>
                            <div className={classes.collapseContent}>
                                <PaymentPickerCreditCard
                                    guestCheckout={guestCheckout}
                                    handleSubmit={async (stripe, elements, values) => {

                                        if (!stripe || !elements) {
                                            return;
                                        }

                                        const result = await stripe.createPaymentMethod({
                                            type: 'card',
                                            card: elements.getElement(CardNumberElement),
                                            billing_details: {
                                                address: {
                                                    city: values.billingCity,
                                                    line1: values.billingAddress1,
                                                    line2: values.billingAddress2,
                                                    postal_code: values.billingZip,
                                                    state: values.billingState
                                                }
                                            }
                                        });

                                        if (result.error) {
                                            setCreditCardErrorMessage(result.error.message);
                                            setModal('payment-method-error-modal');
                                        } else {
                                            const data = {...values, paymentMethod: 'credit_card'};
                                            updateGuestCheckout({
                                                ...data,
                                                ccType: result.paymentMethod.card.brand,
                                                ccLastFour: result.paymentMethod.card.last4,
                                                recaptchaResponse: document.getElementById('g-recaptcha-response').value,
                                                stripePaymentMethodId: result.paymentMethod.id,
                                                billingAccountId: ''
                                            });
                                        }
                                    }}
                                />
                            </div>
                        </Collapse>




                        <ListItem className={classes.listItem} button selected={expanded === 'paypal'} onClick={() => {handleAccordionChange('paypal');}}>
                            <ListItemIcon>
                                <img style={{maxWidth: '46px'}} src="https://res.cloudinary.com/giftya-llc/image/upload/v1667333700/Design%20Assets/Icons/paypal_rxlomm.png" border="0" alt="PayPal Logo"/>
                            </ListItemIcon>
                            <ListItemText primary="PayPal" secondary={guestCheckout.paymentMethod === 'paypal' && `Proceed with PayPal payment method`}/>
                            <ListItemSecondaryAction onClick={() => {handleAccordionChange('paypal');}}>
                                {expanded === 'paypal' ? <ExpandLess /> : <ExpandMore />}
                            </ListItemSecondaryAction>
                        </ListItem>
                        <Collapse in={expanded === 'paypal'} timeout="auto" unmountOnExit={false}>
                            <div className={classes.collapseContent}>
                                <Button
                                    onClick={handleNextWithPayPal}
                                    fullWidth
                                    variant="contained"
                                    color="secondary"
                                    style={{marginBottom: '8px', color: 'white'}}
                                >
                                    Next
                                </Button>
                            </div>
                        </Collapse>






                        <ListItem className={classes.listItem} button selected={expanded === 'apple_pay'} onClick={() => {handleAccordionChange('apple_pay');}}>
                            <ListItemIcon>
                                <img alt="" style={{maxWidth: '46px'}} src="https://res.cloudinary.com/giftya-llc/image/upload/v1667333700/Design%20Assets/Icons/apple_bjbfr6.png"/>
                            </ListItemIcon>
                            <ListItemText primary="Apple Pay"/>
                            <ListItemSecondaryAction onClick={() => {handleAccordionChange('apple_pay');}}>
                                {guestCheckout.paymentMethod === 'apple_pay' && guestCheckout.stripePaymentMethodId && (
                                    <CheckCircleIcon color="secondary"/>
                                )}
                                {expanded === 'apple_pay' ? <ExpandLess /> : <ExpandMore />}
                            </ListItemSecondaryAction>
                        </ListItem>
                        <Collapse in={expanded === 'apple_pay'} timeout="auto" unmountOnExit={false}>
                            <div className={classes.collapseContent}>
                                <WalletButton
                                    paymentIntentClientSecret={guestCheckout.paymentIntentClientSecret}
                                    paymentMethod="apple_pay"
                                    updateGuestCheckout={updateGuestCheckout}
                                    amount={guestCheckout.cartTotal}
                                />
                            </div>
                        </Collapse>



                        <ListItem className={classes.listItem} button selected={expanded === 'google_pay'} onClick={() => {handleAccordionChange('google_pay');}}>
                            <ListItemIcon>
                                <img alt="" style={{maxWidth: '46px'}} src="https://res.cloudinary.com/giftya-llc/image/upload/v1667333700/Design%20Assets/Icons/googlepay_oypuxf.png"/>
                            </ListItemIcon>
                            <ListItemText primary="Google Pay"/>
                            <ListItemSecondaryAction onClick={() => {handleAccordionChange('google_pay');}}>
                                {guestCheckout.paymentMethod === 'google_pay' && guestCheckout.stripePaymentMethodId && (
                                    <CheckCircleIcon color="secondary"/>
                                )}
                                {expanded === 'google_pay' ? <ExpandLess /> : <ExpandMore />}
                            </ListItemSecondaryAction>
                        </ListItem>
                        <Collapse in={expanded === 'google_pay'} timeout="auto" unmountOnExit={false}>
                            <div className={classes.collapseContent}>
                                <WalletButton
                                    paymentIntentClientSecret={guestCheckout.paymentIntentClientSecret}
                                    paymentMethod="google_pay"
                                    updateGuestCheckout={updateGuestCheckout}
                                    amount={guestCheckout.cartTotal}
                                />
                            </div>
                            {guestCheckout.paymentMethod === 'google_pay' && (
                                <Button
                                    href={secureCheckout ? '/secure/checkout' : '/guest-checkout/summary'}
                                    style={{color: 'white'}}
                                    fullWidth
                                    variant="contained"
                                    color="secondary">
                                    Next
                                </Button>
                            )}
                        </Collapse>
                    </List>

                    <PaymentMethodErrorModal
                        errorMessage={creditCardErrorMessage}
                    />

                </div>
            </CardContent>
        </Card>
    );
}

export default connect(
    (state) => ({
        guestCheckout: state.guestCheckout.guestCheckout,
        updateGuestCheckoutSuccess: state.guestCheckout.updateGuestCheckoutSuccess,
        updateGuestCheckoutError: state.guestCheckout.updateGuestCheckoutError,
        updateBillingAddressSuccess: state.stripe.updateBillingAddressSuccess,
        updateBillingAddressError: state.stripe.updateBillingAddressError
    }),
    {
        setFooterOpen,
        updateGuestCheckout,
        updateGuestCheckoutReset,
        setModal,
        setScrollToElement,
        updateBillingAddress,
        updateBillingAddressReset
    }
)(GuestCheckoutPaymentPicker);